import {Component, OnInit} from '@angular/core';
import {AuthenticationService} from "../../_auth/authentication.service";
import {Side} from "../../_modules/x-components/x-models/x-enums/side";
import {GlobalAlertService} from "../../_services/global-alert.service";
import {UserHttpService} from "../../selfservice/x-http-requests/user-http.service";
import {AlertLevel} from "../../enums/alert-level";
import {CommonModule} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {
  BracketedAccount,
  BracketedAccountKind,
  UserAuthenticators
} from "../../models/admin_owner/user/external-identity-info";
import {
  CrossCheckDisplayComponent
} from "../../_modules/x-components/cross-check-display/cross-check-display.component";
import {PayrollRedeemComponent} from "../../_modules/company/payroll/payroll-redeem.component";
import {TwoFactorPanelComponent} from "./two-factor-panel/two-factor-panel.component";
import {AccountLinkOptionsComponent} from "../../_auth/linking/account-link-options.component";
import {describeNativeAccountError} from "../../_auth/local/local-account-errors";
import {readErrorCode} from "../../_auth/two-factor/second-factor-errors";
import {Observable} from "rxjs";

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
  imports: [
    CommonModule, FormsModule, CrossCheckDisplayComponent, PayrollRedeemComponent, TwoFactorPanelComponent,
    AccountLinkOptionsComponent
  ]
})
export class ProfileComponent implements OnInit {
  /** The current user's password-login status + linked external authenticators (local-user session). */
  authenticators?: UserAuthenticators;

  /** Local accounts linked to the current external identity (external-identity session). */
  linkedAccounts: BracketedAccount[] = [];

  /** Exposed so the template can compare `a.kind` against the wire ordinals rather than against strings. */
  protected readonly BracketedAccountKind = BracketedAccountKind;

  /**
   * Display name of the external provider for the current session (e.g. "Microsoft (Entra)").
   *
   * `authSource` mirrors the backend's {@code ExternalIdentityProvider} values: `Google`, `Microsoft` or
   * `Local` for any session established today, plus the historical `Entra` value (#279 deleted the Entra
   * broker, so no NEW sign-in can produce it — but an existing identity minted while it still existed keeps its
   * `Entra` provider, and switching/impersonating into it carries the label along). Kept distinct from
   * `Microsoft` so a genuinely old sign-in never reads as the current direct one.
   */
  get providerName(): string {
    switch (this.AuthenticationService.userValue?.authSource) {
      case 'Entra':
        return 'Microsoft (Entra)';
      case 'Google':
        return 'Google';
      case 'Microsoft':
        return 'Microsoft';
      default:
        return this.AuthenticationService.userValue?.authSource ?? 'externen Anbieter';
    }
  }

  /** Where the user manages their password for the current external provider. */
  get providerAccountUrl(): string {
    switch (this.AuthenticationService.userValue?.authSource) {
      case 'Entra':
      case 'Microsoft':
        return 'https://myaccount.microsoft.com/';
      case 'Google':
        return 'https://myaccount.google.com/';
      default:
        return '';
    }
  }
  pattern = new RegExp('^(?=.*[A-Z])(?=.*[!@#$&*])(?=.*[0-9])(?=.*[a-z]).{8,32}$');
  upper = new RegExp('(?=.*[A-Z])');
  lower = new RegExp('(?=.*[a-z])');
  special = new RegExp('(?=.*[!@#$&*])');
  numbers = new RegExp('(?=.*[0-9])');
  old: string = "";
  password: string = "";
  confirmation: string = "";
  protected readonly Side = Side;

  constructor(public AuthenticationService: AuthenticationService, private gas: GlobalAlertService, private userHttpService: UserHttpService) {
  }

  ngOnInit(): void {
    if (this.AuthenticationService.isExternalSession) {
      this.loadLinkedAccounts();
    } else {
      this.loadAuthenticators();
    }
  }

  // ----- external authenticators (local-user session) -----

  loadAuthenticators() {
    this.AuthenticationService.getAuthenticators().subscribe({
      next: (a) => (this.authenticators = a),
      error: (err) => console.error('Failed to load authenticators', err)
    });
  }

  // ----- linked local accounts (external-identity session) -----

  loadLinkedAccounts() {
    this.AuthenticationService.getLinkedAccounts().subscribe({
      next: (a) => (this.linkedAccounts = a ?? []),
      error: (err) => console.error('Failed to load linked accounts', err)
    });
  }

  /**
   * Whether *Trennen* can do anything for this entry — which is not the same question as whether the person
   * may break the binding.
   *
   * A {@link BracketedAccountKind.PayrollCompany} row is not a bracket at all: it is a role GRANT, listed here
   * because it is another thing this account can reach. There is no link row to remove, so no endpoint could
   * honour the button — taking that access away is a revoke, and it belongs to the access screen. Offering a
   * button that cannot work is worse than offering none.
   */
  protected canUnlink(account: BracketedAccount): boolean {
    return account.kind === BracketedAccountKind.User || account.kind === BracketedAccountKind.Employee;
  }

  /**
   * Breaks the binding between this account and one of the local accounts it brackets.
   *
   * **Routed by kind**, because the two live in different databases and are reached by different ids. Every
   * entry used to go to `unlinkAccount(a.id)` → `DELETE …/identity/accounts/{userId}` → `User.GetById` → null →
   * **404**, which hit every Employee and PayrollCompany row whether or not it was stale. An employee needs its
   * company id too: it lives in a per-company database, so its own id does not identify a row.
   *
   * The last-authenticator safety rule is deliberately NOT restated here — `ExternalIdentityService` refuses to
   * remove an employee's last active authenticator while their password login is off, whichever door asked, and
   * a second copy in the UI is a copy that can drift. The refusal arrives as a structured error and is shown.
   */
  unlinkAccount(account: BracketedAccount) {
    if (!this.canUnlink(account)) return;
    if (!confirm("Dieses lokale Konto von dieser Anmeldung trennen?")) return;

    const request = account.kind === BracketedAccountKind.Employee && account.companyId
      ? this.AuthenticationService.unlinkEmployeeAccount(account.companyId, account.id)
      : this.AuthenticationService.unlinkAccount(account.id);

    request.subscribe({
      next: () => {
        this.gas.createAndShow("Erfolg", "Konto getrennt.", AlertLevel.success);
        this.loadLinkedAccounts();
      },
      error: (err) => this.showActionError(err)
    });
  }

  /**
   * Where a provider must come back to after a door is walked.
   *
   * Pinned to the route rather than read off `location`: this page can be reached carrying
   * `?claimed=success` from a previous round trip, and handing that back to the provider would re-announce a
   * migration that already happened.
   */
  protected get profileReturnUrl(): string {
    return `${location.origin}/authentication/profile`;
  }

  /**
   * The e-mail + password door finished, here, with no navigation.
   *
   * Nothing else has to happen — no session is issued and this one keeps working — but the authenticator list
   * is now a row out of date, and it is the thing on screen that would otherwise still read "Keine externe
   * Authentifizierung verknüpft" beside the account that was just created.
   */
  protected onNativeClaimed(): void {
    this.gas.createAndShow('Erfolg', 'Konto verknüpft.', AlertLevel.success);
    this.loadAuthenticators();
  }

  /** A provider door would not start. Same German mapping as every other failed action on this screen. */
  protected onLinkFailed(err: unknown): void {
    this.showActionError(err);
  }

  removeAuthenticator(identityId: string) {
    if (!confirm("Diese externe Authentifizierung entfernen?")) return;
    this.AuthenticationService.removeAuthenticator(identityId).subscribe({
      next: () => {
        this.gas.createAndShow("Erfolg", "Externe Authentifizierung entfernt.", AlertLevel.success);
        this.loadAuthenticators();
      },
      error: (err) => this.showActionError(err)
    });
  }

  togglePasswordLogin(enabled: boolean) {
    this.AuthenticationService.setPasswordLogin(enabled).subscribe({
      next: () => this.loadAuthenticators(),
      error: (err) => {
        this.showActionError(err);
        this.loadAuthenticators(); // revert the toggle to the server's actual state
      }
    });
  }

  /**
   * Reports a failed profile action, in **German mapped from the error code**.
   *
   * It used to render `err.error.description` — the server's `ErrorCatalog` text, English by design and written
   * for a log rather than for a person (frontend style guide, *"Surfacing an API error"*). The two refusals a
   * reader actually meets here are the account-safety rules, and they arrived as *"Password login can only be
   * disabled while at least one external authenticator is active."* in an otherwise fully German screen. Both
   * are rules somebody has to act on, which is the worst kind of sentence to leave in the wrong language.
   *
   * Shared by all four actions on purpose (unlink, link/claim, remove authenticator, password-login toggle):
   * they answer overlapping codes, and one mapping cannot drift from itself.
   */
  private showActionError(err: any) {
    this.gas.createAndShow("Fehler", ProfileComponent.actionMessageFor(readErrorCode(err)), AlertLevel.error);
  }

  /**
   * The German sentence for a failed profile action.
   *
   * Only the codes these actions genuinely raise are mapped. Everything else — the claim flow's invitation
   * refusals included, which have screens of their own — degrades to the generic sentence rather than to
   * `description`: a code that ships without a mapping must become a fixed German fallback, never the raw
   * field. That is the rule, not a gap to "improve" by reaching for `description` again.
   */
  private static actionMessageFor(code: string | null): string {
    switch (code) {
      // "Password login can only be disabled while at least one external authenticator is active." — the rule
      // that stops an account ending up with no working door at all.
      case 'E_AUTH_PASSWORD_DISABLE_REQUIRES_EXTERNAL':
        return 'Die Passwort-Anmeldung lässt sich nur abschalten, solange mindestens eine externe Anmeldung ' +
          '(Microsoft, Google oder Apple) aktiv ist. Verknüpfen Sie zuerst eine.';
      // Its mirror: removing the last authenticator while the password login is switched off.
      case 'E_AUTH_LAST_EXTERNAL_AUTH_REMOVAL_BLOCKED':
        return 'Die letzte externe Anmeldung kann nicht entfernt werden, solange die Passwort-Anmeldung ' +
          'abgeschaltet ist. Schalten Sie diese zuerst wieder ein.';
      case 'E_AUTH_ACCOUNT_DEACTIVATED':
        return 'Dieses Konto wurde deaktiviert. Bitten Sie um eine neue Einladung — Ihr Konto bleibt dabei ' +
          'erhalten.';
      default:
        return 'Die Aktion konnte nicht ausgeführt werden.';
    }
  }

  CheckUpper(): boolean {
    return this.upper.test(this.password);
  }

  CheckLower(): boolean {
    return this.lower.test(this.password);
  }

  CheckSpecial(): boolean {
    return this.special.test(this.password);
  }

  CheckNumbers(): boolean {
    return this.numbers.test(this.password);
  }

  CheckLength(): boolean {
    return this.password.length >= 8 && this.password.length <= 32
  }

  CheckAll(): boolean {
    return this.pattern.test(this.password);
  }

  Validate(): boolean {
    return this.password == this.confirmation && this.CheckAll() && this.old != this.password && this.old != '';
  }

  /**
   * Changes the signed-in session's own password, **routed by what kind of session is asking**.
   *
   * There are three credential stores behind one form, and only two of them are reachable from here:
   *
   * | session | credential | endpoint |
   * | --- | --- | --- |
   * | native (#279) | `AccountPassword`, keyed on the `ExternalIdentity` | `POST /auth/local/password` |
   * | legacy user / employee | `User.Password` / `Employee.Password` | `PUT /auth/user/changepassword` |
   * | federated | at Google, Microsoft or Apple | none — not ours to change |
   *
   * This used to call the legacy endpoint unconditionally. That endpoint resolves either a management `User` or
   * an `Employee` from the session, and a native session is neither — so the one account kind this whole branch
   * exists for was the one that could not change its own password. See
   * {@link AuthenticationService.isNativeSession} for why "is this native?" is not `authSource === 'Local'`.
   *
   * The federated branch returns without firing anything. The template already renders the provider notice
   * instead of the form, so this guard is unreachable through the UI — it is here because the method is public
   * and the decision belongs with the code that acts on it, not only with the markup that happens to render it
   * today. Posting to the local endpoint from a federated session would answer
   * `E_AUTH_LOCAL_ACCOUNT_REQUIRED`, i.e. a refusal that reads like a bug rather than like the deliberate
   * "that credential lives elsewhere" it is.
   */
  change() {
    if (this.AuthenticationService.isExternalSession) return;

    if (!this.Validate()) {
      this.gas.createAndShow("Fehler", "Bitte überprüfen Sie Ihre Eingaben.", AlertLevel.error);
      return;
    }

    // Typed as `unknown` on purpose: the two endpoints answer different bodies (the native one answers nothing,
    // the legacy one an untyped object) and neither body is read. Without the widening TypeScript refuses the
    // union of the two `subscribe` overloads outright.
    const request: Observable<unknown> = this.AuthenticationService.isNativeSession
      ? this.AuthenticationService.changeLocalPassword(this.old, this.password)
      : this.userHttpService.ChangePassword(this.old, this.password);

    request.subscribe({
      next: () => {
        this.gas.createAndShow("Erfolg", "Das Passwort wurde erfolgreich geändert. Beim nächsten Login bitte das neue Passwort nutzen.", AlertLevel.success);
        this.old = "";
        this.password = "";
        this.confirmation = "";
      },
      error: (err: any) => {
        console.error(err);
        // The native endpoint answers the structured ApiError catalog, so its own German sentence is shown where
        // there is one — a weak password and a wrong current password are both things the person can act on, and
        // a flat "something went wrong" would send them to support instead. The legacy endpoint has no such
        // catalog, which is why the generic sentence stays as the fallback.
        this.gas.createAndShow("Fehler",
          err?.error?.code ? describeNativeAccountError(err.error.code)
            : "Beim Speichern des Passworts ist ein Fehler aufgetreten.",
          AlertLevel.error);
      }
    })
  }
}
